import React, { useMemo, useState } from "react";
import {
  Box,
  TableRow,
  TableCell,
  Typography,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const tdLabelSx = {
  width: 380,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 700,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1.5, verticalAlign: "middle" };
const noteSx = { color: "#666", fontSize: 12, lineHeight: 1.25 };

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#fff",
  p: 3,
  borderRadius: 2,
  width: "min(720px, calc(100vw - 32px))",
  boxShadow: 24,
  outline: "none",
};

const normalizeTipoDocumento = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (v === "FACTURA" || v === "01") return "01";
  if (v === "NOTA_CREDITO" || v === "04") return "04";
  return v;
};

const tipoDocumentoOptions = [
  { value: "01", label: "Factura" },
  { value: "04", label: "Nota de Crédito" },
];

const tipoDocumentoLabel = {
  "01": "Fact",
  "04": "N. de Crédito",
};

const toInt = (v) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : 0;
};

export default function EmissionAndSequenceSettings({
  puntos = [],
  secuenciales = [],
  onRefresh,
  getCSRF,
  setSnack: externalSetSnack,
}) {
  const [snack, setSnackLocal] = useState({ open: false, msg: "", type: "success" });
  const setSnack = typeof externalSetSnack === "function" ? externalSetSnack : setSnackLocal;

  const [puntoOpen, setPuntoOpen] = useState(false);
  const [puntoForm, setPuntoForm] = useState({
    tipo_documento: "01",
    establecimiento: "001",
    punto_emision: "",
  });

  const [secuencialOpen, setSecuencialOpen] = useState(false);
  const [secForm, setSecForm] = useState({
    tipo_documento: "01",
    establecimiento: "001",
    punto_emision: "",
    secuencia_inicial: "",
  });

  const puntosUnicos = useMemo(() => {
    const seen = new Set();
    return puntos
      .filter((p) => {
        const key = `${p.establecimiento}-${p.punto_emision}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((p) => ({
        establecimiento: String(p.establecimiento || "").padStart(3, "0"),
        punto_emision: String(p.punto_emision || "").padStart(3, "0"),
      }));
  }, [puntos]);

  const secuencialesUnicos = useMemo(() => {
    const map = new Map();

    for (const s of secuenciales) {
      const tipo = normalizeTipoDocumento(s.tipo_documento);
      const establecimiento = String(s.establecimiento || "").padStart(3, "0");
      const puntoEmision = String(s.punto_emision || "").padStart(3, "0");
      const secuencia = toInt(
        s.secuencia_inicial ?? s.secuencial_actual ?? s.secuencia_actual ?? 1
      );

      const key = `${tipo}-${establecimiento}-${puntoEmision}`;
      const actual = map.get(key);

      if (!actual || secuencia >= toInt(actual.secuencia_inicial)) {
        map.set(key, {
          ...s,
          tipo_documento: tipo,
          establecimiento,
          punto_emision: puntoEmision,
          secuencia_inicial: secuencia,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const ta = String(a.tipo_documento || "");
      const tb = String(b.tipo_documento || "");
      const ea = String(a.establecimiento || "");
      const eb = String(b.establecimiento || "");
      const pa = String(a.punto_emision || "");
      const pb = String(b.punto_emision || "");

      if (ta !== tb) return ta.localeCompare(tb);
      if (ea !== eb) return ea.localeCompare(eb);
      return pa.localeCompare(pb);
    });
  }, [secuenciales]);

  const getSecuencialActual = (tipoDocumento, establecimiento, puntoEmision) => {
    const tipo = normalizeTipoDocumento(tipoDocumento);
    const est = String(establecimiento || "").padStart(3, "0");
    const pto = String(puntoEmision || "").padStart(3, "0");

    const match = secuencialesUnicos.find(
      (s) =>
        normalizeTipoDocumento(s.tipo_documento) === tipo &&
        String(s.establecimiento || "").padStart(3, "0") === est &&
        String(s.punto_emision || "").padStart(3, "0") === pto
    );

    return match ? String(match.secuencia_inicial).padStart(9, "0") : "000000001";
  };

  const openSecuencialModal = () => {
    const primerSec = secuencialesUnicos[0];
    const primerPunto = puntosUnicos[0];

    const tipoDocumento = primerSec?.tipo_documento || "01";
    const establecimiento = primerSec?.establecimiento || primerPunto?.establecimiento || "001";
    const puntoEmision = primerSec?.punto_emision || primerPunto?.punto_emision || "";

    setSecForm({
      tipo_documento: tipoDocumento,
      establecimiento,
      punto_emision: puntoEmision,
      secuencia_inicial: getSecuencialActual(tipoDocumento, establecimiento, puntoEmision),
    });

    setSecuencialOpen(true);
  };

  const addPunto = async () => {
    try {
      const punto = puntoForm.punto_emision.replace(/\D/g, "").slice(0, 3).padStart(3, "0");

      if (!punto || punto === "000") {
        setSnack({ open: true, type: "error", msg: "Ingrese un punto de emisión válido" });
        return;
      }

      const fd = new FormData();
      fd.append("tipo_documento", normalizeTipoDocumento(puntoForm.tipo_documento));
      fd.append("establecimiento", puntoForm.establecimiento);
      fd.append("punto_emision", punto);

      const r = await fetch("/facturas/puntos/agregar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      const j = await r.json();

      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Punto agregado" });
        setPuntoOpen(false);
        setPuntoForm({
          tipo_documento: "01",
          establecimiento: "001",
          punto_emision: "",
        });
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo agregar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  const deletePunto = async (item) => {
    try {
      const r = await fetch("/facturas/puntos/eliminar/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRF() },
        body: JSON.stringify({
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const j = await r.json();

      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Punto eliminado" });
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo eliminar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  const addSecuencial = async () => {
    try {
      if (!secForm.punto_emision) {
        setSnack({ open: true, type: "error", msg: "Seleccione un punto de emisión" });
        return;
      }

      const fd = new FormData();
      fd.append("tipo_documento", normalizeTipoDocumento(secForm.tipo_documento));
      fd.append("establecimiento", secForm.establecimiento);
      fd.append("punto_emision", secForm.punto_emision);
      fd.append("secuencia_inicial", String(parseInt(secForm.secuencia_inicial || "1", 10)));

      const r = await fetch("/facturas/secuenciales/actualizar/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": getCSRF() },
        body: fd,
      });

      const j = await r.json();

      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Secuencia modificada" });
        setSecuencialOpen(false);
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo modificar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  const deleteSecuencial = async (item) => {
    try {
      const r = await fetch("/facturas/secuenciales/actualizar/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRF() },
        body: JSON.stringify({
          eliminar: 1,
          tipo_documento: normalizeTipoDocumento(item.tipo_documento),
          establecimiento: item.establecimiento,
          punto_emision: item.punto_emision,
        }),
      });

      const j = await r.json();

      if (r.ok) {
        setSnack({ open: true, type: "success", msg: "Secuencia eliminada" });
        await onRefresh();
      } else {
        setSnack({ open: true, type: "error", msg: j.error || "No se pudo eliminar" });
      }
    } catch {
      setSnack({ open: true, type: "error", msg: "Error de conexión" });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell sx={tdLabelSx}>
          Establecimiento - Punto Emisión por defecto
          <Box sx={noteSx}>Al crear un punto se inicializan los secuenciales de todos los documentos disponibles</Box>
        </TableCell>
        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPuntoOpen(true)} sx={{ textTransform: "none" }}>
              Añadir
            </Button>

            {puntosUnicos.map((p) => (
              <Box
                key={`${p.establecimiento}-${p.punto_emision}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  bgcolor: "#fafafa",
                }}
              >
                <Typography>
                  {p.establecimiento} - {p.punto_emision}
                </Typography>
                <Tooltip title="Eliminar punto">
                  <IconButton size="small" onClick={() => deletePunto(p)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={tdLabelSx}>
          Secuencia inicial de Documentos Electrónicos
        </TableCell>
        <TableCell sx={tdFieldSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openSecuencialModal}
              sx={{ textTransform: "none" }}
              disabled={!puntosUnicos.length}
            >
              Modificar
            </Button>

            {secuencialesUnicos.map((s) => (
              <Box
                key={`${s.tipo_documento}-${s.establecimiento}-${s.punto_emision}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  bgcolor: "#fafafa",
                }}
              >
                <Typography>
                  {tipoDocumentoLabel[normalizeTipoDocumento(s.tipo_documento)] || s.tipo_documento} · {s.establecimiento} - {s.punto_emision} -{" "}
                  {String(s.secuencia_inicial).padStart(9, "0")}
                </Typography>
                <Tooltip title="Eliminar secuencial">
                  <IconButton size="small" onClick={() => deleteSecuencial(s)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </TableCell>
      </TableRow>

      <Modal open={puntoOpen} onClose={() => setPuntoOpen(false)}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 750, mb: 2 }}>Crear Punto de Emisión</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Establecimiento</InputLabel>
              <Select label="Establecimiento" value={puntoForm.establecimiento}>
                <MenuItem value="001">001</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Punto Emisión"
              value={puntoForm.punto_emision}
              onChange={(e) =>
                setPuntoForm((p) => ({
                  ...p,
                  punto_emision: e.target.value.replace(/\D/g, "").slice(0, 3),
                }))
              }
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button onClick={() => setPuntoOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={addPunto}>
              Aceptar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={secuencialOpen} onClose={() => setSecuencialOpen(false)}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 800, mb: 2 }}>Secuencia inicial de Documentos Electrónicos</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo Documento</InputLabel>
              <Select
                label="Tipo Documento"
                value={secForm.tipo_documento}
                onChange={(e) =>
                  setSecForm((p) => ({
                    ...p,
                    tipo_documento: e.target.value,
                    secuencia_inicial: getSecuencialActual(e.target.value, p.establecimiento, p.punto_emision),
                  }))
                }
              >
                {tipoDocumentoOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Establecimiento</InputLabel>
              <Select
                label="Establecimiento"
                value={secForm.establecimiento}
                onChange={(e) =>
                  setSecForm((p) => {
                    const nuevoEstablecimiento = e.target.value;
                    const primerPuntoDelEst = puntosUnicos.find((x) => x.establecimiento === nuevoEstablecimiento)?.punto_emision || "";
                    return {
                      ...p,
                      establecimiento: nuevoEstablecimiento,
                      punto_emision: primerPuntoDelEst,
                      secuencia_inicial: getSecuencialActual(p.tipo_documento, nuevoEstablecimiento, primerPuntoDelEst),
                    };
                  })
                }
              >
                {[...new Set(puntosUnicos.map((p) => p.establecimiento))].map((establecimiento) => (
                  <MenuItem key={establecimiento} value={establecimiento}>
                    {establecimiento}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Punto Emisión</InputLabel>
              <Select
                label="Punto Emisión"
                value={secForm.punto_emision}
                onChange={(e) =>
                  setSecForm((p) => ({
                    ...p,
                    punto_emision: e.target.value,
                    secuencia_inicial: getSecuencialActual(p.tipo_documento, p.establecimiento, e.target.value),
                  }))
                }
              >
                {puntosUnicos
                  .filter((p) => p.establecimiento === secForm.establecimiento)
                  .map((p) => (
                    <MenuItem key={`${p.establecimiento}-${p.punto_emision}`} value={p.punto_emision}>
                      {p.punto_emision}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Secuencial Inicial"
              value={secForm.secuencia_inicial}
              onChange={(e) =>
                setSecForm((p) => ({
                  ...p,
                  secuencia_inicial: e.target.value.replace(/\D/g, "").slice(0, 9),
                }))
              }
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button onClick={() => setSecuencialOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={addSecuencial}>
              Aceptar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snack.type} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}